import React, { useCallback, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Flex, Text } from '@adobe/react-spectrum';
import { SORT_MAP } from '../../data/redux/slices/leaderboardSlice';
import './Users.css';
import {
    usersFetchFiltered,
    fetchUsersItems,
    setUsersSortDirection,
    setUsersSortBy,
    setSelectedListIndex,
} from '../../data/redux/slices/usersSlice';
import { getGrades } from '../../data/ScoreDisplayUtils';
import { Helmet } from 'react-helmet';
import { ResultsListing } from '../resultsListing/ResultsListing';
import '../resultsListing/renderers/ResultsCellRenderers.css';
import { capBracketRating, capDisplayValueForSort, capPowerLevel } from '../../data/displayCaps';
import { setAppRoute } from '../../data/redux/slices/appSlice';
import { BreadCrumbs } from '../breadcrumbs/BreadCrumbs';
import { NitroAd } from '../../ads/NitroAd';
import { fetchUserDetails } from '../../data/redux/slices/userDetailsSlice';
import HeroPanel from '../common/heroPanel/HeroPanel';
import { useFilterUrlSync } from '../../utils/useFilterUrlSync';
import { isDefaultFilterState } from '../../utils/filterUrl';

export function Users() {
    const dispatch = useDispatch();
    const rawListItems = useSelector((state) => state?.users?.listItems);
    // Memoise the filtered list — without this the inline `.filter` returns
    // a new array on every render and defeats any downstream memoisation
    // in ResultsListing.
    const listItems = useMemo(() => (rawListItems || []).filter((item) => item && item?.id), [rawListItems]);
    const nextCursor = useSelector((state) => state?.users?.nextCursor);
    const isFetching = useSelector((state) => state?.users?.isFetching);
    const loadingState = useSelector((state) => state?.users?.loadingState);
    const searchFilters = useSelector((state) => state?.users?.filters);
    const sortBy = useSelector((state) => state?.users?.sortBy);
    const previewIsShowing = useSelector((state) => state.preview.isShowing);
    const selectedListKey = useSelector((state) => state.users.selectedListKey);
    const selectedListIndex = useSelector((state) => state.users.selectedListIndex);
    const sortDirection = useSelector((state) => state.users.sortDirection);

    const handleSortChange = (sortIndex) => {
        dispatch(setUsersSortBy(SORT_MAP[sortIndex], searchFilters));
        dispatch(fetchUsersItems(null, true, SORT_MAP[sortIndex], sortDirection));
    };

    const handleSortDirectionChange = (direction) => {
        dispatch(setUsersSortDirection(direction));
        dispatch(fetchUsersItems(null, true, sortBy, direction, searchFilters));
    };

    const handleFiltersChange = (filters) => {
        dispatch(usersFetchFiltered(filters, sortBy));
    };

    useFilterUrlSync({
        context: 'users',
        filters: searchFilters,
        sortBy,
        sortDirection,
        onApply: ({ filters, sortBy: urlSortBy, sortDirection: urlSortDirection }) => {
            if (isDefaultFilterState(filters, urlSortBy, urlSortDirection, 'users')) return;
            dispatch(setUsersSortBy(urlSortBy));
            dispatch(setUsersSortDirection(urlSortDirection));
            dispatch(usersFetchFiltered(filters, urlSortBy, urlSortDirection));
        },
    });

    const handleIndexSelection = (evn) => {
        dispatch(setSelectedListIndex(evn));
    };

    const getCellRenderer = (item, columnKey, options = {}) => {
        // /search?type=users projection: { userId, username, deckCount,
        // avgPowerLevel, avgSaltRating, avgBracketRating, colorIdentities }.
        // sortBy.enum drives which avg-rating to highlight in the score cell.
        const sortToAvgField = {
            POWERLEVEL: 'avgPowerLevel',
            SALT: 'avgSaltRating',
            BRACKET: 'avgBracketRating',
        };
        switch (columnKey) {
            case 'score':
                // getGrades expects { powerLevelRating, saltRating, bracketRating }.
                // /search?type=users serves these as avg* — adapt for the lookup.
                const gradesInput = {
                    powerLevelRating: item?.avgPowerLevel,
                    saltRating: item?.avgSaltRating,
                    bracketRating: item?.avgBracketRating,
                };
                const grade = getGrades(gradesInput);
                const prop = sortBy?.enum.toLowerCase();

                if (sortBy?.enum === SORT_MAP.COUNT.enum) {
                    const deckCount = Number(item?.deckCount) || 0;
                    return (
                        <span>
                            <Flex direction={'column'} justifyContent={'space-around'} alignItems={'center'}>
                                <span data-role={'large'} style={{ color: grade?.[prop]?.color }}>
                                    {deckCount}
                                </span>
                                <span data-role={'small'} style={{ marginTop: 5 }}>
                                    DECK{deckCount === 1 ? '' : 'S'}
                                </span>
                            </Flex>
                        </span>
                    );
                }

                const avgField = sortToAvgField[sortBy?.enum] || 'avgPowerLevel';
                const score = capDisplayValueForSort(item?.[avgField] || 0, sortBy?.enum);
                let floatValue = parseFloat(/[0-9]*\.[0-9]/.exec(score.toString())?.[0] || `${score}`);
                if (!floatValue.toString().includes(`.`)) {
                    floatValue = `${floatValue}.0`;
                }

                return (
                    <span>
                        <Flex direction={'column'} justifyContent={'space-around'} alignItems={'center'}>
                            <span data-role={'large'} style={{ color: grade?.[prop]?.color }}>
                                {floatValue}
                            </span>
                            <span data-role={'small'} style={{ marginTop: 5 }}>
                                {item?.deckCount ?? 0} DECK{item?.deckCount === 1 ? '' : 'S'}
                            </span>
                        </Flex>
                    </span>
                );
            case 'user': {
                const showTier =
                    item?.perks?.showSubscriberLevelOnProfile && item?.perks?.tier && item.perks.tier !== 'none';
                const tierLabel = parseInt(item?.perks?.tier) ? `L${item.perks.tier}` : 'MAX';
                const bio = (item?.bio || '').trim();
                return (
                    <div>
                        <Flex direction={'column'} minHeight={60} justifyContent={'center'} rowGap={4}>
                            <Flex direction={'row'} alignItems={'center'} columnGap={8}>
                                <Text data-role={'small-header'}>{item?.username || item?.id}</Text>
                                {item?.isDiscordMod && (
                                    <span className="userBadge userBadge--mod" aria-label="Discord moderator">
                                        MOD
                                    </span>
                                )}
                                {showTier && (
                                    <span className="userBadge userBadge--supporter">
                                        {tierLabel}&nbsp;SUPPORTER
                                    </span>
                                )}
                            </Flex>
                            {bio && <span className="userBio">{bio}</span>}
                        </Flex>
                    </div>
                );
            }
            case 'userAvatarUrl': {
                // /search?type=users now returns avatarUri when the user has
                // uploaded one; fall through to the blank avatar for the rest.
                const avatarUri = (item?.avatarUri || '').trim();
                const fallback = '/resources/blank-user-avatar.png';
                return (
                    <div>
                        <div className="AvatarCell">
                            <img
                                src={avatarUri || fallback}
                                width="50"
                                height="50"
                                alt={item?.username || item?.id}
                                className="AvatarCell"
                                loading="lazy"
                                onError={(e) => {
                                    // Some uploaded avatars 404 over time; degrade
                                    // gracefully instead of leaving a broken-image icon.
                                    if (e.currentTarget.src !== window.location.origin + fallback) {
                                        e.currentTarget.src = fallback;
                                    }
                                }}
                            />
                        </div>
                    </div>
                );
            }
            case 'row_footer': {
                const formatAvg = (value, decimals = 1) => {
                    const n = Number(value);
                    if (!Number.isFinite(n) || n <= 0) return null;
                    return n.toFixed(decimals);
                };
                const formatSaltAvg = (value) => {
                    const n = Number(value);
                    if (!Number.isFinite(n) || n <= 0) return null;
                    return n >= 100 ? Math.round(n).toString() : n.toFixed(1);
                };
                const isNarrow =
                    Boolean(options?.compact) ||
                    (typeof window !== 'undefined' && window.innerWidth && window.innerWidth < 700);
                const chips = [];
                if (sortBy?.enum !== SORT_MAP.POWERLEVEL.enum) {
                    const v = formatAvg(capPowerLevel(item?.avgPowerLevel), 1);
                    if (v) chips.push({ key: 'power', label: 'PWR', value: v });
                }
                if (sortBy?.enum !== SORT_MAP.BRACKET.enum) {
                    const v = formatAvg(capBracketRating(item?.avgBracketRating), 1);
                    if (v) chips.push({ key: 'bracket', label: isNarrow ? 'BRKT' : 'BRACKET', value: v });
                }
                if (sortBy?.enum !== SORT_MAP.SALT.enum) {
                    const v = formatSaltAvg(item?.avgSaltRating);
                    if (v) chips.push({ key: 'salt', label: 'SALT', value: v });
                }
                if (chips.length === 0) return null;
                return (
                    <div className="resultsRowMetricStrip">
                        {chips.map((chip) => (
                            <span key={chip.key} className={`resultsRowMetric resultsRowMetric--${chip.key}`}>
                                <span className="resultsRowMetricLabel">{chip.label}</span>
                                <span className="resultsRowMetricValue">{chip.value}</span>
                            </span>
                        ))}
                    </div>
                );
            }
            default:
                return (
                    <div>
                        <div className="TextCell">{item[columnKey]}</div>
                    </div>
                );
        }
    };

    const getColumnRenderer = (item) => {
        let content;

        switch (item.uid) {
            case 'saltRating':
                content = <div />;
                break;
            case 'userAvatarUrl':
                content = <div />;
                break;
            default:
                content = item.name;
        }

        return <div style={{ minWidth: '83px' }}>{content}</div>;
    };

    const handleSelectionChange = (id) => {
        // Click-through routes to user-details. Author-detail pages stay
        // reachable from deck pages but are no longer the browse target.
        dispatch(fetchUserDetails(id));
        dispatch(setAppRoute({ route: `/user/id/${id}` }));
    };

    const handleLoadMore = () => {
        if (!isFetching && nextCursor !== null) {
            dispatch(fetchUsersItems(nextCursor, false, sortBy, sortDirection));
        }
    };

    // Stable identity so ResultsListing doesn't re-create NavLinks each render.
    const userItemRoute = useCallback((item) => `/user/id/${item?.id}`, []);

    const max = window.innerWidth - 180;
    let columns = [
        { name: 'avatar', uid: 'userAvatarUrl', maxWidth: 65, minWidth: 65, width: 65 },
        { name: 'User', uid: 'user', maxWidth: max, minWidth: 200, width: max },
        { name: '', uid: 'score', width: 120 },
    ];

    const helmetName = `Users`;
    const helmetCanonical = `https://www.commandersalt.com/users`;
    const helmetDescription = `The saltiest MtG deck builders on the planet!`;
    const helmetTitle = `Commandersalt.com - The saltiest MtG deck builders on the planet!`;

    return (
        <Flex direction="column">
            <Helmet>
                <title>{helmetName}</title>
                <meta name="creator" content="Ben Rimbey" />
                <link rel="canonical" href={helmetCanonical} />
                <meta name="description" content={helmetDescription} />
                <meta name="twitter:title" content={helmetTitle} />
                <meta property="og:site_name" content="Commandersalt.com" />
                <meta property="og:title" content={helmetTitle} />
                <meta property="og:image" content="https://www.commandersalt.com/chef-kiss-meta-image.png" />
                <meta property="og:description" content={helmetDescription} />
            </Helmet>
            <BreadCrumbs />
            <div className="heroPanelWrap browseHeroWrap">
                <HeroPanel
                    tone="gold"
                    eyebrow="Browse · Users"
                    title="The hands behind the salt"
                    hint="Sort by their average ratings or by how many decks they've submitted. Tap a user to see their full deck list and a breakdown of their build tendencies."
                />
            </div>
            <NitroAd />
            <ResultsListing
                includeSortByCount={true}
                sortBy={sortBy}
                sortDirection={sortDirection}
                handleSortChange={handleSortChange}
                handleSortDirectionChange={handleSortDirectionChange}
                disableNavChanges={true}
                allowDeckPreview={false}
                handleSelectionChange={handleSelectionChange}
                handleIndexSelection={handleIndexSelection}
                itemRoute={userItemRoute}
                hasMore={nextCursor !== null}
                selectedListIndex={selectedListIndex}
                selectedListIndexPrefix={'users_'}
                selectedListKey={selectedListKey}
                columns={columns}
                columnRenderer={getColumnRenderer}
                listItems={listItems}
                cellRenderer={getCellRenderer}
                previewIsShowing={previewIsShowing}
                handleLoadMore={handleLoadMore}
                loadingState={loadingState}
                showFilterPanel={true}
                filterContext="users"
                onFiltersChange={handleFiltersChange}
                filters={searchFilters}
            />
        </Flex>
    );
}
